import { Notifier } from '@/lib/notifier'
import { en, type Dictionary } from './en'
import { da } from './da'

/* Minimal i18n: a typed dictionary registry with a live `tr` binding.
 * Adding a language = create `<code>.ts` typed as Dictionary and register
 * it here — the type checker then guarantees no string is missed. No i18n
 * framework: ~30 strings do not justify one.
 *
 * Locale resolution (browser only — node/vitest always get the default):
 *   1. ?lang=<code> URL override (explicit);
 *   2. localStorage — the switcher's persisted choice (explicit);
 *   3. alarms/{id}.locale — OPTIONAL hint from the mobile app, applied via
 *      applyAlarmLocale() once the alarm doc loads, ONLY when the viewer
 *      has no explicit choice; never persisted (a different alarm may
 *      carry a different language);
 *   4. navigator.language prefix match (da-DK -> da);
 *   5. DEFAULT_LOCALE (en). */

const dictionaries = { en, da } satisfies Record<string, Dictionary>

export type LocaleCode = keyof typeof dictionaries

export const DEFAULT_LOCALE: LocaleCode = 'en'

const STORAGE_KEY = 'h2h.locale'

function isLocaleCode(v: string | null | undefined): v is LocaleCode {
  return v != null && v in dictionaries
}

function detect(): { code: LocaleCode; explicit: boolean } {
  if (typeof window === 'undefined') return { code: DEFAULT_LOCALE, explicit: false }
  const override = new URLSearchParams(window.location.search).get('lang')
  if (isLocaleCode(override)) return { code: override, explicit: true }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (isLocaleCode(stored)) return { code: stored, explicit: true }
  } catch {
    /* private mode / blocked storage — fall through */
  }
  const lang = (navigator.language ?? '').toLowerCase().split('-')[0] ?? ''
  if (isLocaleCode(lang)) return { code: lang, explicit: false }
  return { code: DEFAULT_LOCALE, explicit: false }
}

const initial = detect()

/** Current locale code — also sent to getDirections for API-side texts. */
export let locale: LocaleCode = initial.code

/** True once the viewer has explicitly chosen (?lang, stored choice, or the
 * switcher) — an explicit choice always beats the alarm's hint. */
let userChose = initial.explicit

/** Live ES-module binding — importers always see the current dictionary. */
export let tr: Dictionary = dictionaries[locale]

/* React cannot see through module bindings — locale changes publish through
 * this notifier; App subscribes and re-keys the router subtree. */
class LocaleNotifier extends Notifier {
  publish(): void {
    this.notifyListeners()
  }
}
export const localeNotifier = new LocaleNotifier()

/** The switcher: explicit choice — persisted and published. */
export function setLocale(code: LocaleCode): void {
  userChose = true
  locale = code
  tr = dictionaries[code]
  try {
    window.localStorage.setItem(STORAGE_KEY, code)
  } catch {
    /* non-persistent is fine */
  }
  localeNotifier.publish()
}

/** The alarm's OPTIONAL locale hint: applied only when the viewer has not
 * chosen; unknown/absent values are ignored; NEVER persisted. */
export function applyAlarmLocale(code: string | null | undefined): void {
  if (userChose) return
  if (!isLocaleCode(code) || code === locale) return
  locale = code
  tr = dictionaries[code]
  localeNotifier.publish()
}

export type { Dictionary }
